import React, { useState, useContext, useMemo, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  FormLayout,
  ChoiceList,
  Checkbox,
  TextField,
  Select,
  Banner,
  Link,
  Text,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import ProgressIndicator from "../../../../components/components/ProgressIndicator";
import { TitleBar } from "@shopify/app-bridge-react";
import { AppContext } from "../../../../components/global/appContext";
import { useUpdateMapSettings } from "../../../../hooks/queries/mapSettings";
import useCompare from "../../../../hooks/useCompare";
import {mobileBreadcrumb, mobileTitle } from "../../../../utilities/mobile";


export default function MapSettingsForm({ initialMapSettings }) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  const [mapSettings, setMapSettings] = useState(initialMapSettings);

  const hasUnsavedChanges = !useCompare(mapSettings, initialMapSettings);

  const updateMapSettingsMutation = useUpdateMapSettings({
    onSuccess: (data) => {
      setMapSettings(data);
      appContext.setMessage(t("settings.map.messages.savedSuccessfully"));
    },
    onError: (error) => {
      setMapSettings(error.response.data);
      appContext.setMessage(t("settings.map.messages.couldntSave"), true);
    },
  });

  function handleUpdate(key, value) {
    setMapSettings((oldMapSettings) => ({
      ...oldMapSettings,
      map_settings: { ...oldMapSettings.map_settings, [key]: value },
    }));
  }

  const { announcement, errors, allowed_to_hide_whitelabel, managed_token } =
    mapSettings;

  const formatErrorFor = (key) => {
    if (errors && errors[key]) {
      const errorMessage = errors[key].join(" & ");
      return errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
    }
  };

  const {
    mapbox_token,
    mapbox_style,
    geolocate_on_load,
    filter_behaviour,
    keep_search_in_view,
    distance_units,
    max_results,
    max_distance,
    show_popups,
    hide_whitelabel,
    search_results_locations,
    search_results_locations_prefix,
    sort_by_priority,
  } = mapSettings.map_settings;

  // Handle form submission (triggered by Save Bar)
  const handleSubmit = (e) => {
    e.preventDefault();
    updateMapSettingsMutation.mutate(mapSettings.map_settings);
  };

  // Handle form reset (triggered by Discard button in Save Bar)
  const handleReset = (e) => {
    e.preventDefault();
    setMapSettings(initialMapSettings);
  };

  return (
    <form 
      data-save-bar 
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
    <Page>
      <TitleBar
        //{...titleAndBreadcrumb("Mapasd", [{ content: "Settings" }]) }
        title={mobileTitle(t("settings.map.title"), t("settings.common.breadcrumbs.settings"))}
        breadcrumbs={[{ content: t("settings.common.breadcrumbs.settings") }]}
        actionGroups={[]}
      />
      <Layout>
        {announcement && (
          <Layout.Section>
            <Banner status={announcement.status} title={announcement.title}>
              <p>{announcement.text}</p>
            </Banner>
          </Layout.Section>
        )}
        <Layout.AnnotatedSection
          title={t("settings.map.mapboxToken.title")}
          description={t("settings.map.mapboxToken.description")}
        >
          <Card>
            <Card.Section>
              <TextField
                label={t("settings.map.mapboxToken.label")}
                placeholder={
                  !managed_token
                    ? t("settings.map.mapboxToken.placeholder")
                    : t("settings.map.mapboxToken.placeholderManaged")
                }
                value={mapbox_token}
                onChange={(value) => handleUpdate("mapbox_token", value)}
                monospaced={mapbox_token}
                helpText={
                  <Text variant="bodyMd" as="span">{t("settings.map.mapboxToken.helpText").split("instructions for creating a Mapbox key")[0]}{" "}
                    <Link url="https://docs.sosuper.app/article/35-setting-up-a-mapbox-token">
                      {t("settings.map.mapboxToken.linkInstructions")}
                    </Link>{t("settings.map.mapboxToken.helpText").split("get in touch for assistance")[0].split("instructions for creating a Mapbox key")[1]}{" "}
                    <Link
                      onClick={() => {
                        window.Beacon("open");
                        window.Beacon("prefill", {
                          subject: "Mapbox Key Help",
                        });
                        window.Beacon("navigate", "/ask/message/");
                      }}
                    >
                      {t("settings.map.mapboxToken.linkSupport")}
                    </Link>.</Text>
                }
                autoComplete="off"
                error={formatErrorFor("mapbox_token")}
              />
            </Card.Section>
            {managed_token && (
              <Card.Section>
                <div style={{ marginBottom: "0.4rem" }}>
                  <ProgressIndicator
                    title={t("settings.map.managedToken.title")}
                    used={managed_token.map_loads}
                    of={managed_token.max_map_loads}
                    color="highlight"
                    // size="small"
                  />
                </div>
                <Text  variant="bodyMd" as="span" color="subdued">{t("settings.map.managedToken.description", { max: managed_token.max_map_loads })}</Text>
              </Card.Section>
            )}
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title={t("settings.map.appearance.title")}
          description={t("settings.map.appearance.description")}
        >
          <MapStyleSelector
            value={mapbox_style}
            onChange={(value) => handleUpdate("mapbox_style", value)}
          />
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title={t("settings.map.searchBehaviour.title")}
          description={t("settings.map.searchBehaviour.description")}
        >
          <Card>
            <Card.Section>
              <FormLayout>
                <Checkbox
                  label={t("settings.map.searchBehaviour.geolocateLabel")}
                  helpText={t("settings.map.searchBehaviour.geolocateHelp")}
                  checked={geolocate_on_load}
                  onChange={(value) => handleUpdate("geolocate_on_load", value)}
                />
              </FormLayout>
            </Card.Section>
            <Card.Section>
              <FormLayout>
                <ChoiceList
                  title={t("settings.map.searchBehaviour.filterBehaviour.title")}
                  choices={[
                    {
                      label: t("settings.map.searchBehaviour.filterBehaviour.all.label"),
                      value: "all",
                      helpText: t("settings.map.searchBehaviour.filterBehaviour.all.help"),
                    },
                    {
                      label: t("settings.map.searchBehaviour.filterBehaviour.any.label"),
                      value: "any",
                      helpText: t("settings.map.searchBehaviour.filterBehaviour.any.help"),
                    },
                  ]}
                  selected={filter_behaviour}
                  onChange={(value) =>
                    handleUpdate("filter_behaviour", value[0])
                  }
                />
              </FormLayout>
            </Card.Section>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title={t("settings.map.searchResults.title")}
          description={t("settings.map.searchResults.description")}
        >
          <Card sectioned>
            <FormLayout>
              <Checkbox
                label={t("settings.map.searchBehaviour.keepSearchInView.label")}
                helpText={t("settings.map.searchBehaviour.keepSearchInView.help")}
                checked={keep_search_in_view}
                onChange={(value) => handleUpdate("keep_search_in_view", value)}
              />
              <ChoiceList
                title={t("settings.map.searchBehaviour.distanceUnits.title")}
                choices={[
                  { label: t("settings.map.searchBehaviour.distanceUnits.miles"), value: "mi" },
                  { label: t("settings.map.searchBehaviour.distanceUnits.km"), value: "km" },
                ]}
                selected={distance_units}
                onChange={(value) => handleUpdate("distance_units", value[0])}
              />
              <FormLayout.Group>
                <Select
                  label={t("settings.map.searchBehaviour.maxResults.label")}
                  options={[
                    { label: "10 " + t("settings.map.searchBehaviour.maxResults.options.results30").split(" ")[1], value: "10" },
                    { label: "15 " + t("settings.map.searchBehaviour.maxResults.options.results30").split(" ")[1], value: "15" },
                    { label: "20 " + t("settings.map.searchBehaviour.maxResults.options.results30").split(" ")[1], value: "20" },
                    { label: "25 " + t("settings.map.searchBehaviour.maxResults.options.results30").split(" ")[1], value: "25" },
                    { label: t("settings.map.searchBehaviour.maxResults.options.results30"), value: "30" },
                  ]}
                  value={String(max_results)}
                  onChange={(value) =>
                    handleUpdate("max_results", parseInt(value))
                  }
                />
                <Select
                  label={t("settings.map.searchBehaviour.maxDistance.label")}
                  options={[
                    { label: "25 km", value: "25" },
                    { label: "50 km", value: "50" },
                    { label: "100 km", value: "100" },
                    { label: "250 km", value: "250" },
                    { label: t("settings.map.searchBehaviour.maxDistance.options.unlimited"), value: "0" },
                  ]}
                  value={String(max_distance)}
                  onChange={(value) =>
                    handleUpdate("max_distance", parseInt(value))
                  }
                />
              </FormLayout.Group>
              <Checkbox
                label={t("settings.map.searchBehaviour.showLocationNames.label")}
                helpText={t("settings.map.searchBehaviour.showLocationNames.help")}
                checked={search_results_locations}
                onChange={(value) =>
                  handleUpdate("search_results_locations", value)
                }
              />
              <Checkbox
                label={t("settings.map.searchBehaviour.sortByPriority.label")}
                checked={sort_by_priority}
                onChange={(value) =>
                  handleUpdate("sort_by_priority", value)
                }
                helpText={
                  <Text variant="bodyMd" as="span">
                    {t("settings.map.searchBehaviour.sortByPriority.help")}
                  </Text>
                }
              />
              {search_results_locations ? (
                <TextField
                  label="Prefix location name"
                  labelHidden
                  prefix="Prefix with:"
                  value={search_results_locations_prefix}
                  onChange={(value) =>
                    handleUpdate("search_results_locations_prefix", value)
                  }
                />
              ) : null}
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection title={t("settings.map.popups.title")} description="">
          <Card sectioned>
            <FormLayout>
              <Checkbox
                label={t("settings.map.popups.showPopups.label")}
                helpText={t("settings.map.popups.showPopups.help")}
                checked={show_popups}
                onChange={(value) => handleUpdate("show_popups", value)}
              />
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection title={t("settings.map.whitelabel.title")} description="">
          <Card sectioned>
            <FormLayout>
              <Checkbox
                label={t("settings.map.whitelabel.hideLabel")}
                disabled={!allowed_to_hide_whitelabel}
                checked={hide_whitelabel}
                onChange={(value) => handleUpdate("hide_whitelabel", value)}
                helpText={
                  !allowed_to_hide_whitelabel && (
                    <Text variant="bodyMd" as="span">Requires<Link url="/plan">superest plan</Link>
                    </Text>
                  )
                }
              />
            </FormLayout>
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
    </Page>
    </form>
  );
}

function MapStyleSelector({ value, onChange }) {
  const { t } = useTranslation();
  const { mapboxAdminToken } = useContext(AppContext);
  const prefix = "mapbox://styles/mapbox/";
  const styleID = useMemo(() => value.replace(prefix, ""), [value]);

  useEffect(() => console.log(styleID), [styleID]);

  const updateStyleID = (styleID) => onChange(prefix + styleID);

  const styleOptions = [
    { label: t("settings.map.mapStyle.options.streets"), value: "streets-v11" },
    { label: t("settings.map.mapStyle.options.outdoors"), value: "outdoors-v11" },
    { label: t("settings.map.mapStyle.options.light"), value: "light-v10" },
    { label: t("settings.map.mapStyle.options.dark"), value: "dark-v10" },
    { label: t("settings.map.mapStyle.options.satellite"), value: "satellite-v9" },
    { label: t("settings.map.mapStyle.options.satelliteStreets"), value: "satellite-streets-v11" },
    { label: t("settings.map.mapStyle.options.navigationDay"), value: "navigation-day-v1" },
    { label: t("settings.map.mapStyle.options.navigationNight"), value: "navigation-night-v1" },
  ];

  const mapboxPreviewImageURL = useMemo(() => {
    const URLParams = new URLSearchParams({
      access_token: mapboxAdminToken,
      style: styleID,
      logo: false,
      attribution: false,
    });
    const coords = [-73.99, 40.7].join(",");
    const zoom = 11;
    const size = "500x500";
    return `https://api.mapbox.com/styles/v1/mapbox/${styleID}/static/${coords},${zoom}/${size}?${URLParams.toString()}`;
  }, [styleID, mapboxAdminToken]);

  return (
    <Card>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          borderRadius: "var(--p-border-radius-2)",
          overflow: "hidden",
        }}
      >
        <div style={{ flex: 1 }}>
          <Card.Section>
            <FormLayout>
              <ChoiceList
                title={t("settings.map.mapStyle.title")}
                choices={styleOptions}
                selected={[styleID]}
                onChange={updateStyleID}
                allowMultiple={false}
              />
            </FormLayout>
          </Card.Section>
        </div>
        <div
          style={{
            backgroundImage: `url('${mapboxPreviewImageURL}')`,
            backgroundSize: "cover",
            backgroundColor: "var(--p-surface-subdued)",
            backgroundPosition: "center",
            flex: 1,
            borderLeft: "1px solid var(--p-border-subdued)",
          }}
        ></div>
      </div>
    </Card>
  );
}

// {
//   label: "Custom",
//   value: "custom",
//   renderChildren: () =>
//     value == "custom" && (
//       <TextField
//         label="Custom Map Style URL"
//         autoComplete="off"
//         value={value}
//         onChange={onChange}
//         labelHidden
//         helpText="Paste your mapbox style URL here"
//       />
//     ),
// },

// const styleValues = useMemo(
//   () => styleOptions.map(({ value }) => value),
//   // .filter((value) => value != "custom"),
//   [styleOptions]
// );

// const styleInOptions = useMemo(
//   () => styleValues.includes(styleID),
//   [styleID, styleValues]
// );
